// Integrationen on the web: which agents report (seen in the last 30 days), the devices that record them, and how to
// connect more. Hooks are installed on the machine itself (Mac app or CLI); the browser never touches agent configs.
import { useMemo, useState } from 'react';
import { switchGuest } from '../api/guest.ts';
import { ProLock } from '../components/ProLock.tsx';
import type { Device } from '../api/types.ts';
import { dayRange } from '../lib/calendar.ts';
import { providerInfo } from '../model/data.ts';
import { Confirm, Loading, ScreenHeader, SectionHeader } from '../components/common.tsx';
import { useFmt, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button, IntegrationTile, LiveDot } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';

const PROVIDERS: { id: string; description: string; methods: string[]; soon?: boolean }[] = [
  { id: 'claude-code', description: 'Hooks in ~/.claude/settings.json. Zählt auch Subagents.', methods: ['Hooks'] },
  { id: 'codex', description: 'Hooks für die Codex-CLI, Session-Logs für die Codex-App und VS Code.', methods: ['Hooks', 'Session-Logs'] },
  { id: 'cursor', description: 'Hooks in ~/.cursor/hooks.json. Teilabdeckung: Cursor meldet keine Freigabe-Wartezeiten.', methods: ['Hooks'] },
  { id: 'gemini-cli', description: 'Hooks in ~/.gemini/settings.json für die Gemini-CLI.', methods: ['Hooks'] },
  { id: 'github-copilot', description: 'Hooks für die Copilot-CLI und den Agent-Modus in VS Code. Teilabdeckung: Freigaben enden erst mit dem Werkzeug.', methods: ['Hooks'] },
];

export function Integrations() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(30_000);
  const range = useMemo(() => ({ start: dayRange(now, store.timeZone, -29).start, end: dayRange(now, store.timeZone).end }), [Math.floor(now / 60_000), store.timeZone]);
  const loaded = useRange(range);
  const devices = store.me?.devices ?? [];
  const deviceName = (id: string) => devices.find((d) => d.id === id)?.name;
  const [removing, setRemoving] = useState<Device | null>(null);
  return (
    <div className="screen">
      <ScreenHeader title={t('Integrationen')}
        subtitle={t('Tellary hört deinen Agents auf deinen Rechnern zu. Verbunden wird dort: in der Mac-App oder mit der Tellary-CLI. Prompts, Code und Tool-Ausgaben verlassen den Rechner nie.')} />
      {!loaded.data ? <Loading label={t('Lädt …')} /> : (
        <div className="tiles">
          {PROVIDERS.map((p) => {
            const info = providerInfo(p.id);
            const sessions = loaded.data?.sessions.filter((s) => s.provider === p.id) ?? [];
            const last = sessions.reduce((n, s) => Math.max(n, s.lastEventAt ?? s.startedAt), 0);
            const where = [...new Set(sessions.map((s) => deviceName(s.deviceId)).filter((x): x is string => !!x))].slice(0, 2).join(', ');
            const status = p.soon ? 'soon' : last > 0 ? 'connected' : 'available';
            const detail = status === 'connected' ? [t('Zuletzt %@', fmt.relative(last, now)), ...(where ? [where] : [])].join(' · ')
              : status === 'available' ? t('In den letzten 30 Tagen keine Sessions') : undefined;
            return <IntegrationTile key={p.id} name={info.name} mono={info.mono} provider={p.id} description={t(p.description)} methods={p.methods.map((x) => t(x))} status={status} {...(detail ? { detail } : {})} />;
          })}
        </div>
      )}

      <div className="stack">
        <SectionHeader title={t('Geräte')} caption={t('Jedes Gerät hat einen eigenen Schlüssel. Entfernen sperrt ihn sofort.')}>
          {store.api.guest || !store.pro ? null : <Button size="sm" icon="plus" onClick={() => store.openSheet({ kind: 'addDevice' })}>{t('Gerät hinzufügen')}</Button>}
        </SectionHeader>
        <div className="tl-card table">
          {store.api.guest ? (
            <div className="note rules__empty guest-devices">
              <span>{t('Geräte und Agents melden an dein Konto. Als Gast erfasst du hier nur deine eigene Zeit.')}</span>
              <Button size="sm" variant="primary" onClick={() => switchGuest(false)}>{t('Konto erstellen oder anmelden')}</Button>
            </div>
          ) : null}
          {!store.api.guest && !store.pro ? (
            <div className="rules__empty"><ProLock text={t('Server und CI melden mit Pro automatisch an dein Konto.')} /></div>
          ) : null}
          {store.api.guest ? null : devices.length === 0 ? <p className="note rules__empty">{t('Noch kein Gerät. Melde dich in der Mac-App an oder füge einen Rechner mit der CLI hinzu.')}</p> : devices.map((d) => {
            const seen = d.last_seen_at ? Date.parse(d.last_seen_at) : null;
            return (
              <div key={d.id} className="table__row device-row">
                <span className="device-row__icon"><Icon name={d.platform.toLowerCase().startsWith('mac') ? 'laptop' : 'server'} size={18} /></span>
                <span className="device-row__text">
                  <span className="device-row__name">{d.name}</span>
                  <span className="note">{[platformName(d.platform), seen ? t('zuletzt %@', fmt.relative(seen, now)) : t('noch nie gemeldet')].join(' · ')}</span>
                </span>
                {seen && now - seen < 10 * 60_000 ? <span className="device-row__live"><LiveDot /></span> : <span />}
                <Button variant="plain" size="sm" onClick={() => setRemoving(d)}>{t('Entfernen …')}</Button>
              </div>
            );
          })}
        </div>
      </div>

      <section className="tl-card panel cli-card">
        <h3 className="card-title">{t('Eigene Agents und Skripte')}</h3>
        <p className="note">{t('Jeder Prozess kann Tellary melden, wann er arbeitet. Gezählt wird wie bei allen Agents nur „Arbeitet“.')}</p>
        <pre className="code-block">{'tally event prompt --session build-42 --provider my-agent\ntally event tool --session build-42 --provider my-agent\ntally event stop --session build-42 --provider my-agent'}</pre>
        <p className="note note--faint">{t('Auf einem Rechner ohne Mac-App sendet die CLI direkt an diesen Server: Gerät hinzufügen, dann tally login.')}</p>
      </section>

      {removing ? (
        <Confirm title={t('„%@“ entfernen?', removing.name)} danger confirm={t('Entfernen')} cancel={t('Abbrechen')} onClose={() => setRemoving(null)}
          message={t('Das Gerät kann danach nichts mehr senden oder abgleichen. Seine bisherigen Sessions bleiben erhalten.')}
          onConfirm={() => { void store.api.removeDevice(removing.id).then(() => store.refreshMe()).catch((e: unknown) => { store.problem = store.describe(e); void store.refreshMe(); }); }} />
      ) : null}
    </div>
  );
}

/** Headless devices register as "linux"; lists show the platform's name. */
function platformName(platform: string): string {
  const names: Record<string, string> = { linux: 'Linux', windows: 'Windows' };
  return names[platform.toLowerCase()] ?? platform;
}
